import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
  StorageOutlined as StorageOutlinedIcon,
  BoltOutlined as BoltOutlinedIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

/** Purpose: show platform-level administration metrics and shortcuts. Why: platform operators need cross-tenant visibility distinct from bot workspaces. */
export default function AdminDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // User management dialogs
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'USER', status: 'active' });
  
  // Bot assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState(null);
  const [assignBotId, setAssignBotId] = useState('');
  const [assignEnvs, setAssignEnvs] = useState(['dev']);
  
  // Bot creation dialog
  const [botDialogOpen, setBotDialogOpen] = useState(false);
  const [botForm, setBotForm] = useState({
    botName: '',
    description: '',
    createProdEnvironment: false,
    adminEmail: '',
    adminPassword: '',
    adminName: '',
  });
  const [showBotPassword, setShowBotPassword] = useState(false);
  const [botDialogError, setBotDialogError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creatingBot, setCreatingBot] = useState(false);
  
  // TenantId change dialog
  const [tenantIdDialogOpen, setTenantIdDialogOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [tenantIdForm, setTenantIdForm] = useState({ tenantId: '' });
  const [updatingTenantId, setUpdatingTenantId] = useState(false);

  // Environment provisioning and access management
  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);
  const [environmentBot, setEnvironmentBot] = useState(null);
  const [environmentUserId, setEnvironmentUserId] = useState('');
  const [environmentAccess, setEnvironmentAccess] = useState(['dev']);
  const [environmentBusy, setEnvironmentBusy] = useState(false);
  const [storageUsage, setStorageUsage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [selectedStorageUser, setSelectedStorageUser] = useState(null);

  const { isAdmin, fetchUserInfo } = useAuth();

  const activeUsers = users.filter((user) => user.status === 'active').length;
  const adminUsers = users.filter((user) => user.role === 'ADMIN').length;
  /** Purpose: Handle assigned bots in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const assignedBots = users.reduce((total, user) => total + (user.botAccess?.length || 0), 0);
  /** Purpose: Resolve total environments for the admin dashboard module. Why: The surrounding operation uses this value to make its decision. */
  const totalEnvironments = bots.reduce((total, bot) => total + (bot.envCount || bot.environments?.length || 0), 0);
  const activeBotCount = bots.filter((bot) => bot.status === 'active').length;
  const pendingUsers = users.filter((user) => user.status !== 'active').length;
  const recentLoginCount = users.filter((user) => user.lastLogin).length;

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
      fetchBots();
      fetchStorageUsage();
    }
  }, []);

  /** Purpose: Load fetch users for the admin dashboard module. Why: The selected view needs current data to render or continue processing. */
  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/v1/admin/users');
      setUsers((response.data.users || []).map((user) => ({ ...user, id: user.id || user._id })));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    }
  };

  /** Purpose: Load fetch bots for the admin dashboard module. Why: The selected view needs current data to render or continue processing. */
  const fetchBots = async () => {
    try {
      const response = await axios.get('/api/v1/admin/bots');
      setBots(response.data.bots || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load bots');
    }
  };

  /** Purpose: Load fetch storage usage for the admin dashboard module. Why: The selected view needs current data to render or continue processing. */
  const fetchStorageUsage = async (refresh = false) => {
    setStorageLoading(true);
    try {
      const response = await axios.get(`/api/v1/admin/storage-usage${refresh ? '?refresh=true' : ''}`);
      setStorageUsage(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load MongoDB usage');
    } finally {
      setStorageLoading(false);
    }
  };

  /** Purpose: Perform format bytes for the admin dashboard module. Why: Callers need a consistent result from this shared operation. */
  const formatBytes = (bytes = 0) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / (1024 ** unitIndex)).toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  };

  /** Purpose: Handle handle update user in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleUpdateUser = async () => {
    try {
      const userId = selectedUser?.id || selectedUser?._id;
      if (!userId) {
        setError('User ID is missing. Refresh the page and try again.');
        return;
      }
      await axios.put(`/api/v1/admin/users/${userId}`, {
        role: userForm.role,
        status: userForm.status,
      });
      fetchUsers();
      setUserDialogOpen(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  /** Purpose: Handle handle create bot in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleCreateBot = async () => {
    const trimmedBotName = (botForm.botName || '').trim();
    if (!trimmedBotName) {
      setBotDialogError('Bot name is required');
      return;
    }
    const trimmedEmail = (botForm.adminEmail || '').trim();
    if (!trimmedEmail) {
      setBotDialogError('Admin email is required so the bot owner can log in');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setBotDialogError('Please enter a valid email address');
      return;
    }
    if (!botForm.adminPassword || botForm.adminPassword.length < 6) {
      setBotDialogError('Password is required and must be at least 6 characters');
      return;
    }

    setCreatingBot(true);
    setBotDialogError(null);
    setError(null);

    try {
      const response = await axios.post('/api/v1/admin/bots', {
        botName: trimmedBotName,
        description: botForm.description,
        createProdEnvironment: botForm.createProdEnvironment,
        adminEmail: trimmedEmail,
        adminPassword: botForm.adminPassword,
        adminName: botForm.adminName,
      });

      setBotDialogOpen(false);
      setSuccess(
        response.data?.message ||
        `Bot ${response.data?.bot?.botId || trimmedBotName} created! Login assigned to ${trimmedEmail}.`
      );
      setBotForm({
        botName: '',
        description: '',
        createProdEnvironment: false,
        adminEmail: '',
        adminPassword: '',
        adminName: '',
      });
      fetchBots();
      fetchUsers();
    } catch (err) {
      setBotDialogError(err.response?.data?.error || 'Failed to create bot');
    } finally {
      setCreatingBot(false);
    }
  };

  /** Purpose: Handle handle update tenant id in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleUpdateTenantId = async () => {
    if (!tenantIdForm.tenantId) {
      setError('Tenant ID is required');
      return;
    }

    if (!selectedBot) {
      setError('No bot selected');
      return;
    }

    setUpdatingTenantId(true);
    setError(null);

    try {
      await axios.put(`/api/v1/admin/bots/${selectedBot.botId}/tenant`, {
        tenantId: tenantIdForm.tenantId,
      });
      setTenantIdDialogOpen(false);
      setSelectedBot(null);
      setTenantIdForm({ tenantId: '' });
      setError(null);
      alert('Tenant ID updated successfully for bot and all its flows!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update tenant ID');
    } finally {
      setUpdatingTenantId(false);
    }
  };

  /** Purpose: Handle handle assign bot in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleAssignBot = async () => {
    try {
      await axios.post(`/api/v1/admin/users/${assignUserId}/bots/${assignBotId}`, {
        environmentsAllowed: assignEnvs,
      });
      fetchUsers();
      setAssignDialogOpen(false);
      setAssignUserId(null);
      setAssignBotId('');
      setAssignEnvs(['dev']);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign bot');
    }
  };

  /** Purpose: Handle open environment manager in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openEnvironmentManager = (bot) => {
    setEnvironmentBot(bot);
    setEnvironmentUserId('');
    setEnvironmentAccess(bot.environments?.some((env) => env.envName === 'dev') ? ['dev'] : []);
    setEnvironmentDialogOpen(true);
    setError(null);
  };

  /** Purpose: Handle handle create production in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleCreateProduction = async () => {
    if (!environmentBot) return;
    setEnvironmentBusy(true);
    setError(null);
    try {
      await axios.post(`/api/v1/admin/bots/${environmentBot.botId}/environments`, {
        envName: 'prod',
        cloneFrom: 'dev',
      });
      await fetchBots();
      setEnvironmentBot((current) => current ? {
        ...current,
        environments: [...(current.environments || []), { envName: 'prod', isActive: true }],
        envCount: (current.envCount || 0) + 1,
      } : current);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create Production environment');
    } finally {
      setEnvironmentBusy(false);
    }
  };

  /** Purpose: Handle handle save environment access in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleSaveEnvironmentAccess = async () => {
    if (!environmentBot || !environmentUserId || environmentAccess.length === 0) return;
    setEnvironmentBusy(true);
    setError(null);
    try {
      await axios.post(`/api/v1/admin/users/${environmentUserId}/bots/${environmentBot.botId}`, {
        environmentsAllowed: environmentAccess,
      });
      await fetchUsers();
      setEnvironmentDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update environment access');
    } finally {
      setEnvironmentBusy(false);
    }
  };

  /** Purpose: Handle handle remove access in the admin dashboard module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleRemoveAccess = async (userId, botId) => {
    if (!window.confirm('Remove bot access from this user?')) return;
    
    try {
      await axios.delete(`/api/v1/admin/users/${userId}/bots/${botId}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove access');
    }
  };

  if (!isAdmin()) {
    return (
      <Container>
        <Alert severity="error">Access denied. Admin privileges required.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ maxWidth: 1480, mx: 'auto', py: { xs: 0.5, md: 1.5 } }}>
      <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" sx={{ display: 'block', lineHeight: 1, letterSpacing: '0.2em', color: 'primary.main', fontWeight: 800 }}>
              Administration
            </Typography>
            <Typography variant="h4" component="h1" sx={{ mt: 0.75, fontWeight: 800, letterSpacing: '-0.04em' }}>
              Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Users, access, bots and tenant configuration.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setUserForm({ name: '', email: '', role: 'USER', status: 'active' });
                setSelectedUser(null);
                setUserDialogOpen(true);
              }}
            >
              Add User
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setBotForm({
                  botName: '',
                  description: '',
                  createProdEnvironment: false,
                  adminEmail: '',
                  adminPassword: '',
                  adminName: '',
                });
                setBotDialogError(null);
                setBotDialogOpen(true);
              }}
            >
              Create Bot
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            mt: { xs: 2.5, md: 3 },
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
            gap: { xs: 1, md: 1.25 },
          }}
        >
          {[
            { label: 'Total users', value: users.length, icon: <GroupsOutlinedIcon fontSize="small" />, tone: 'rgba(59,130,246,0.10)' },
            { label: 'Active users', value: activeUsers, icon: <VerifiedOutlinedIcon fontSize="small" />, tone: 'rgba(16,185,129,0.10)' },
            { label: 'Admin accounts', value: adminUsers, icon: <ShieldOutlinedIcon fontSize="small" />, tone: 'rgba(14,165,233,0.10)' },
            { label: 'Assigned access', value: assignedBots, icon: <BoltOutlinedIcon fontSize="small" />, tone: 'rgba(168,85,247,0.10)' },
            { label: 'Total Mongo', value: storageLoading && !storageUsage ? '…' : formatBytes(storageUsage?.database?.totalAllocatedBytes), detail: `${(storageUsage?.database?.documentCount || 0).toLocaleString()} documents`, icon: <StorageOutlinedIcon fontSize="small" />, tone: 'rgba(245,158,11,0.10)' },
          ].map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              sx={{
                p: { xs: 1.5, md: 1.75 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: 'none',
                borderTop: '3px solid',
                borderTopColor: 'primary.main',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 0.4, fontWeight: 800, letterSpacing: '-0.04em' }}>
                    {item.value}
                  </Typography>
                  {item.detail && <Typography variant="caption" color="text.secondary">{item.detail}</Typography>}
                </Box>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: item.tone,
                    color: 'primary.main',
                  }}
                >
                  {item.icon}
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          px: { xs: 0.5, sm: 1 },
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{
            minHeight: 44,
            p: 0.5,
            borderRadius: 999,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTab-root': {
              minHeight: 38,
              textTransform: 'none',
              fontWeight: 700,
              px: 2.25,
              borderRadius: 999,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                bgcolor: 'background.paper',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              },
            },
          }}
        >
          <Tab label={`Users (${users.length})`} />
          <Tab label={`Bots (${bots.length})`} />
        </Tabs>

        <Typography variant="body2" color="text.secondary">
          {totalEnvironments} environments total
        </Typography>
      </Box>

      {tabValue === 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 2,
          }}
        >
          <Box>
            <Box
              sx={{
                mb: 1.5,
                p: 2,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>User Management</Typography>
                    <Typography variant="body2" color="text.secondary">Review access, roles, login activity and bot storage.</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Chip label={`${activeUsers} active • ${pendingUsers} pending`} variant="outlined" sx={{ borderRadius: 999, fontWeight: 600 }} />
                    <IconButton size="small" title="Refresh MongoDB usage" onClick={() => fetchStorageUsage(true)} disabled={storageLoading}>
                      {storageLoading ? <CircularProgress size={17} /> : <RefreshOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto',
                bgcolor: 'background.paper',
                boxShadow: '0 16px 36px rgba(15, 23, 42, 0.05)',
              }}
            >
              <Table size="small" sx={{
                '& .MuiTableCell-root': { py: 1.5 },
                '& .MuiTableRow-root:hover': { bgcolor: 'action.hover' },
              }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Bot ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Mongo Size</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Bot Access</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Last Login</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ fontWeight: 700 }}>{user.name}</TableCell>
                      <TableCell>
                        {user.botAccess?.length ? (
                          <Stack spacing={0.5} alignItems="flex-start">
                            {user.botAccess.map((access) => (
                              <Typography key={access.botId} variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                {access.botId}
                              </Typography>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={user.role === 'ADMIN' ? 'primary' : 'default'}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 999 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          color={user.status === 'active' ? 'success' : 'default'}
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 999 }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.botAccess?.length ? (
                          <Chip
                            size="small"
                            icon={<StorageOutlinedIcon />}
                            label={formatBytes(user.botAccess.reduce((total, access) => {
                              const botUsage = storageUsage?.bots?.find((bot) => bot.botId === access.botId);
                              return total + (botUsage?.dataBytes || 0);
                            }, 0))}
                            variant="outlined"
                            color="primary"
                            clickable
                            onClick={() => setSelectedStorageUser(user)}
                            sx={{ fontWeight: 700 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.botAccess && user.botAccess.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {user.botAccess.map((access, idx) => (
                              <Chip
                                key={idx}
                                label={`${access.botName} · ${String(access.accessLevel || 'developer').toUpperCase()}`}
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 999 }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No bots assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedUser(user);
                              setUserForm({
                                name: user.name,
                                email: user.email,
                                role: user.role,
                                status: user.status,
                              });
                              setUserDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAssignUserId(user.id || user._id);
                              setAssignDialogOpen(true);
                            }}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              height: 'fit-content',
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 1, letterSpacing: '0.16em', color: 'primary.main', fontWeight: 800 }}>
                Workspace pulse
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 0.75, fontWeight: 800 }}>
                At a glance
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              {[
                { label: 'Users', value: users.length },
                { label: 'Active users', value: activeUsers },
                { label: 'Pending users', value: pendingUsers },
                { label: 'Recent logins', value: recentLoginCount },
                { label: 'Assigned bot access', value: assignedBots },
                { label: 'Environments', value: totalEnvironments },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    p: 1.25,
                    minHeight: 78,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 0.7, fontWeight: 800, letterSpacing: '-0.03em' }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                Helpful next steps
              </Typography>
              <Stack spacing={0.25}>
                {[
                  'Create a bot workspace',
                  'Assign user access',
                  'Check tenant IDs before publishing',
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      py: 0.65,
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: 10, mt: 0.6, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} px={0.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Bot Registry
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage bot metadata, ownership, and tenant mapping.
              </Typography>
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Bot ID</TableCell>
                  <TableCell>Bot Name</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Environments</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bots.map((bot) => (
                  <TableRow key={bot.botId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                        {bot.botId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{bot.botName}</TableCell>
                    <TableCell>
                      {bot.createdBy?.name || bot.createdBy?.email || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bot.status}
                        color={bot.status === 'active' ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>{bot.userCount}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75}>
                        {(bot.environments || []).map((environment) => (
                          <Chip
                            key={environment.envId || environment.envName}
                            label={environment.envName.toUpperCase()}
                            size="small"
                            color={environment.envName === 'prod' ? 'success' : 'primary'}
                            variant={environment.isActive === false ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 800 }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openEnvironmentManager(bot)}
                        title="Manage environments and access"
                      >
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedBot(bot);
                          setTenantIdForm({ tenantId: '' });
                          setTenantIdDialogOpen(true);
                        }}
                        title="Change Tenant ID"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* User Edit Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={userForm.name}
            onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
            margin="normal"
            required
            disabled={!!selectedUser}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
            margin="normal"
            required
            disabled={!!selectedUser}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={userForm.role}
              onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="USER">USER</MenuItem>
              <MenuItem value="ADMIN">ADMIN</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={userForm.status}
              onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bot Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Bot Access</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Bot</InputLabel>
            <Select
              value={assignBotId}
              onChange={(e) => {
                const nextBotId = e.target.value;
                /** Purpose: Resolve next bot for the admin dashboard module. Why: The surrounding operation uses this value to make its decision. */
                const nextBot = bots.find((bot) => bot.botId === nextBotId);
                setAssignBotId(nextBotId);
                setAssignEnvs(nextBot?.environments?.some((environment) => environment.envName === 'dev') ? ['dev'] : []);
              }}
              label="Bot"
            >
              {bots.map((bot) => (
                <MenuItem key={bot.botId} value={bot.botId}>
                  {bot.botName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Allow Environments:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assignEnvs.includes('dev')}
                  disabled={!bots.find((bot) => bot.botId === assignBotId)?.environments?.some((environment) => environment.envName === 'dev' && environment.isActive !== false)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setAssignEnvs([...assignEnvs, 'dev']);
                    } else {
                      setAssignEnvs(assignEnvs.filter(e => e !== 'dev'));
                    }
                  }}
                />
              }
              label="Development"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={assignEnvs.includes('prod')}
                  disabled={!bots.find((bot) => bot.botId === assignBotId)?.environments?.some((environment) => environment.envName === 'prod' && environment.isActive !== false)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setAssignEnvs([...assignEnvs, 'prod']);
                    } else {
                      setAssignEnvs(assignEnvs.filter(e => e !== 'prod'));
                    }
                  }}
                />
              }
              label="Production"
            />
            {assignBotId && !bots.find((bot) => bot.botId === assignBotId)?.environments?.some((environment) => environment.envName === 'prod') && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Production is not created for this bot. Open Environment Management from the Bots tab first.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignBot}
            variant="contained"
            disabled={!assignBotId || assignEnvs.length === 0}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* User's assigned bot MongoDB usage details */}
      <Dialog open={Boolean(selectedStorageUser)} onClose={() => setSelectedStorageUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>MongoDB Usage Details</DialogTitle>
        <DialogContent>
          {selectedStorageUser && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedStorageUser.name}</Typography>
                <Typography variant="body2" color="text.secondary">Usage across assigned bots</Typography>
              </Box>
              {(selectedStorageUser.botAccess || []).map((access) => {
                const botUsage = storageUsage?.bots?.find((bot) => bot.botId === access.botId);
                return (
                  <Paper key={access.botId} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                      <Box><Typography sx={{ fontWeight: 800 }}>{access.botName}</Typography><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{access.botId}</Typography></Box>
                      <Box sx={{ textAlign: { sm: 'right' } }}><Typography sx={{ fontWeight: 800 }}>{formatBytes(botUsage?.dataBytes)}</Typography><Typography variant="caption" color="text.secondary">{(botUsage?.documentCount || 0).toLocaleString()} documents</Typography></Box>
                    </Stack>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Collection</TableCell><TableCell align="right">Documents</TableCell><TableCell align="right">Data size</TableCell></TableRow></TableHead>
                      <TableBody>
                        {(botUsage?.collections || []).map((collection) => (
                          <TableRow key={collection.collection}><TableCell>{collection.collection}</TableCell><TableCell align="right">{collection.documentCount.toLocaleString()}</TableCell><TableCell align="right">{formatBytes(collection.dataBytes)}</TableCell></TableRow>
                        ))}
                        {!botUsage?.collections?.length && <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">No direct bot-owned documents.</Typography></TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </Paper>
                );
              })}
              <Alert severity="info">Shared/global records and MongoDB index overhead are included in the dashboard total, not in this bot estimate.</Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedStorageUser(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Environment Management Dialog */}
      <Dialog open={environmentDialogOpen} onClose={() => !environmentBusy && setEnvironmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Environments & Access</DialogTitle>
        <DialogContent>
          {environmentBot && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{environmentBot.botName}</Typography>
                <Typography variant="caption" color="text.secondary">{environmentBot.botId}</Typography>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.25 }}>Available environments</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {(environmentBot.environments || []).map((environment) => (
                    <Chip key={environment.envId || environment.envName} label={environment.envName.toUpperCase()} color={environment.envName === 'prod' ? 'success' : 'primary'} />
                  ))}
                  {!environmentBot.environments?.some((environment) => environment.envName === 'prod') && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleCreateProduction}
                      disabled={environmentBusy || !environmentBot.environments?.some((environment) => environment.envName === 'dev')}
                    >
                      Create Production
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Production is cloned from the current Development configuration. User access is granted separately below.
                </Typography>
              </Paper>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Grant user access</Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={environmentUserId}
                    label="User"
                    onChange={(event) => {
                      const userId = event.target.value;
                      /** Purpose: Build user within the admin dashboard module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
                      const user = users.find((candidate) => (candidate.id || candidate._id) === userId);
                      const existing = user?.botAccess?.find((access) => access.botId === environmentBot.botId);
                      setEnvironmentUserId(userId);
                      setEnvironmentAccess(existing?.environmentsAllowed?.length ? existing.environmentsAllowed : ['dev']);
                    }}
                  >
                    {users.filter((user) => user.status === 'active').map((user) => (
                      <MenuItem key={user.id || user._id} value={user.id || user._id}>{user.name} ({user.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1}>
                  {(environmentBot.environments || []).filter((environment) => environment.isActive !== false).map((environment) => (
                    <FormControlLabel
                      key={environment.envName}
                      control={<Checkbox
                        checked={environmentAccess.includes(environment.envName)}
                        onChange={(event) => setEnvironmentAccess((current) => event.target.checked
                          ? [...new Set([...current, environment.envName])]
                          : current.filter((name) => name !== environment.envName))}
                      />}
                      label={environment.envName === 'prod' ? 'Production' : 'Development'}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvironmentDialogOpen(false)} disabled={environmentBusy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEnvironmentAccess}
            disabled={environmentBusy || !environmentUserId || environmentAccess.length === 0}
          >
            {environmentBusy ? <CircularProgress size={22} /> : 'Save Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Bot Dialog */}
      <Dialog open={botDialogOpen} onClose={() => setBotDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Create New Bot</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Bot ID will be auto-generated in format: ZR-{new Date().toISOString().split('T')[0].replace(/-/g, '')}-001
          </Alert>

          {botDialogError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setBotDialogError(null)}>
              {botDialogError}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Bot Details
          </Typography>

          <TextField
            fullWidth
            label="Bot Name"
            placeholder="e.g. Acme Support Bot"
            value={botForm.botName}
            onChange={(e) => setBotForm({ ...botForm, botName: e.target.value })}
            margin="dense"
            required
            autoFocus
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            placeholder="Brief description of this bot and its purpose"
            value={botForm.description}
            onChange={(e) => setBotForm({ ...botForm, description: e.target.value })}
            margin="dense"
            multiline
            rows={2}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25 }}>
            Bot Login Credentials (Admin Account)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            The owner/client will use this email and password to log in and manage this bot.
          </Typography>

          <TextField
            fullWidth
            type="email"
            label="Client / Admin Email"
            placeholder="client@company.com"
            value={botForm.adminEmail}
            onChange={(e) => setBotForm({ ...botForm, adminEmail: e.target.value })}
            margin="dense"
            required
            autoComplete="new-email"
          />

          <TextField
            fullWidth
            type={showBotPassword ? 'text' : 'password'}
            label="Password"
            placeholder="Min. 6 characters"
            value={botForm.adminPassword}
            onChange={(e) => setBotForm({ ...botForm, adminPassword: e.target.value })}
            margin="dense"
            required
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowBotPassword(!showBotPassword)}
                    edge="end"
                  >
                    {showBotPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Owner / Admin Name (Optional)"
            placeholder="e.g. Acme Admin"
            value={botForm.adminName}
            onChange={(e) => setBotForm({ ...botForm, adminName: e.target.value })}
            margin="dense"
          />

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={botForm.createProdEnvironment}
                onChange={(e) => setBotForm({ ...botForm, createProdEnvironment: e.target.checked })}
              />
            }
            label="Also create Production environment"
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
            If checked, both Development and Production environments will be created. Otherwise, only Development will be created.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setBotDialogOpen(false)} disabled={creatingBot}>Cancel</Button>
          <Button
            onClick={handleCreateBot}
            variant="contained"
            disabled={creatingBot || !botForm.botName || !botForm.adminEmail || !botForm.adminPassword}
            sx={{ fontWeight: 600, px: 2.5 }}
          >
            {creatingBot ? <CircularProgress size={22} color="inherit" /> : 'Create Bot & User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Tenant ID Dialog */}
      <Dialog open={tenantIdDialogOpen} onClose={() => setTenantIdDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Tenant ID for Bot</DialogTitle>
        <DialogContent>
          {selectedBot && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will update the tenantId for bot <strong>{selectedBot.botName}</strong> ({selectedBot.botId}) and ALL its flows.
                This action cannot be undone easily.
              </Alert>
              <TextField
                fullWidth
                label="Bot ID"
                value={selectedBot.botId}
                margin="normal"
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="New Tenant ID"
                value={tenantIdForm.tenantId}
                onChange={(e) => setTenantIdForm({ tenantId: e.target.value })}
                margin="normal"
                required
                placeholder="Enter new tenant ID (UUID format)"
                helperText="This will update tenantId for all flows of this bot"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantIdDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateTenantId}
            variant="contained"
            color="warning"
            disabled={updatingTenantId || !tenantIdForm.tenantId}
          >
            {updatingTenantId ? <CircularProgress size={24} /> : 'Update Tenant ID'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
